import { CheckIcon, HomeIcon, LockIcon, MoonIcon, OrganizationIcon, PlusIcon, SearchIcon, SignOutIcon, SunIcon, TriangleDownIcon } from "@primer/octicons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { HttpError, hasSession, signOut, token, repoFromPath, rp } from "../api/client";
import { useMe, useRepo, useWhoami } from "../api/hooks";
import type { Actor } from "../api/types";
import { cn } from "../lib/utils";
import { Login } from "../pages/Login";
import { CommandPalette, useCommandPaletteHotkey } from "./CommandPalette";
import { AvatarImage } from "./AvatarImage";
import { Avatar, BeeLogo, Empty, Loading, Page } from "./common";
import { type Theme, useTheme } from "./theme";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Kbd } from "./ui/kbd";
import { Tooltip } from "./ui/tooltip";

/** The repo in the URL's `/owner/repo`, or null on a top-level page. */
function useRepoFromPath(): string | null {
  return repoFromPath(useLocation().pathname) || null;
}

const crumb =
  "inline-flex h-8 min-w-0 items-center rounded-md px-1.5 leading-6 text-foreground hover:bg-transparent-hover hover:no-underline active:bg-transparent-active";

/** GitHub's AppHeader: logo, the organization › repo breadcrumb, search and the user menu.
 * Organization and repo pages continue the band with their own tabs (SubNav); their sub-pages are a side menu beside the content. */
export function Layout() {
  const me = useWhoami();
  // Before the early returns below: hooks must run on every render.
  const mine = useMe().data;
  const qc = useQueryClient();
  const nav = useNavigate();
  const { pathname, search } = useLocation();
  const repo = useRepoFromPath();
  const [theme, toggleTheme] = useTheme();
  const [palette, setPalette] = useState(false);
  const openPalette = useCallback(() => setPalette(true), []);
  useCommandPaletteHotkey(openPalette);
  const actor = me.data;
  const onSignOut = async () => {
    await signOut();
    qc.clear();
    nav("/login");
  };
  const top = pathname.split("/")[1] ?? "";
  const second = decodeURIComponent(pathname.split("/")[2] ?? "");
  const owner = (top === "orgs" && second !== "" && second !== "new") || top === "profile";
  const signedIn = !!actor && actor.sub !== "anonymous";
  // Beta: unsigned visitors only see sign-in, not the marketing page or the app.
  // Wait for whoami when a token/session exists so a signed-in user never flashes the login page.
  const hadSignIn = !!(token.get() || hasSession());
  // While whoami is pending, or when the server could not answer it, show a bare shell: the full header would offer a
  // sign-in button and an empty account switcher, and an outage must not look like being signed out.
  const down = hadSignIn && me.isError && !(me.error instanceof HttpError && me.error.status < 500);
  if (!signedIn && hadSignIn && (me.isLoading || down)) {
    return (
      <>
        <header className="flex items-center gap-3 border-b bg-header p-4">
          <Link to="/" className="grid size-8 shrink-0 place-items-center rounded-full text-foreground" aria-label="bee home">
            <BeeLogo size={32} />
          </Link>
        </header>
        {down ? (
          <Empty bee="idle">
            <p className="font-semibold text-foreground">The server is not responding</p>
            <p className="mt-1">It may be starting up or busy. Your session is still saved.</p>
            <Button className="mt-4" onClick={() => void me.refetch()} disabled={me.isFetching}>
              {me.isFetching ? "Trying…" : "Try again"}
            </Button>
          </Empty>
        ) : (
          <Page narrow>
            <Loading />
          </Page>
        )}
      </>
    );
  }
  if (!signedIn) {
    if (pathname === "/" || pathname === "/login") return <Login />;
    return <Navigate to={`/login?next=${encodeURIComponent(pathname + search)}`} replace />;
  }
  // The organization in view: a repo's owner (the first path segment), the organization page's, or your own.
  const orgs = mine?.orgs ?? [];
  const org = repo
    ? decodeURIComponent(top)
    : top === "orgs" && owner
      ? second
      : (orgs.find((o) => o.name === mine?.user?.name.toLowerCase()) ?? orgs[0])?.name;
  // Repo and organization pages continue the band with their own tabs (SubNav), which carries the bottom border.
  const tabbed = !!repo || (top === "orgs" && owner);
  // A top-level page that is neither an organization's nor a repo's names itself after the organization.
  const page = top === "new" ? "New repository" : top === "orgs" && second === "new" ? "New organization" : null;
  return (
    // A full-height column, so a page with a side menu can draw its divider down to the bottom of the screen.
    <div className="flex min-h-dvh flex-col">
      <header className={cn("flex items-center gap-3 bg-header px-4 pt-4", tabbed ? "pb-1" : "border-b pb-4")}>
        <Link to="/" className="grid size-8 shrink-0 place-items-center rounded-full text-foreground" aria-label="bee home">
          <BeeLogo size={32} />
        </Link>
        <nav aria-label="Breadcrumbs" className="flex min-w-0 flex-1 items-center gap-1">
          {top === "profile" ? (
            <Link to="/profile?tab=account" className={cn(crumb, "font-semibold")}>
              <span className="truncate">{actor.sub}</span>
            </Link>
          ) : (
            <>
              <ContextSwitcher current={org ?? actor.sub} />
              {repo ? (
                <RepoCrumb repo={repo} />
              ) : (
                page && (
                  <>
                    <span className="shrink-0 text-muted-foreground">/</span>
                    <span className={cn(crumb, "font-semibold")}>
                      <span className="truncate">{page}</span>
                    </span>
                  </>
                )
              )}
            </>
          )}
        </nav>
        <button
          className="flex h-8 min-w-48 flex-[0_1_24rem] items-center gap-2 rounded-md border bg-transparent px-2 text-muted-foreground hover:bg-transparent-hover max-md:w-8 max-md:min-w-0 max-md:flex-none max-md:justify-center max-md:px-0"
          onClick={openPalette}
          aria-label="Search or jump to"
        >
          <SearchIcon size={16} />
          <span className="min-w-0 flex-1 truncate text-left max-md:hidden">Type to search</span>
          <Kbd className="max-md:hidden">⌘K</Kbd>
        </button>
        <UserMenu actor={actor} theme={theme} onToggleTheme={toggleTheme} onSignOut={onSignOut} />
      </header>
      <Outlet />
      <CommandPalette open={palette} onOpenChange={setPalette} theme={theme} onToggleTheme={toggleTheme} />
    </div>
  );
}

/** "/ repo" after the organization, as GitHub's heading. */
function RepoCrumb({ repo }: { repo: string }) {
  const q = useRepo(repo);
  return (
    <>
      <span className="shrink-0 text-muted-foreground">/</span>
      <Link to={rp(repo)} className={crumb}>
        <span className="truncate font-semibold">{repo}</span>
      </Link>
      {q.data?.info.frozen && (
        <Tooltip content="Landing is frozen" side="bottom">
          <Badge tone="red">
            <LockIcon size={12} /> Frozen
          </Badge>
        </Tooltip>
      )}
    </>
  );
}

/** Switch between the organizations you belong to (your personal one included), or start a new one. */
function ContextSwitcher({ current }: { current: string }) {
  const mine = useMe().data;
  const personal = mine?.actor.sub ?? current;
  // Your personal organization is already the home entry.
  const orgs = (mine?.orgs ?? []).filter((o) => o.name !== personal);
  const mark = (name: string) => (name === current ? <CheckIcon size={16} className="ml-auto" /> : null);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={cn(crumb, "gap-1 font-semibold data-[state=open]:bg-transparent-active")} aria-label="Switch account or organization">
        <span className="truncate">{current}</span>
        <TriangleDownIcon size={16} className="shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuGroup className="p-2">
          <DropdownMenuItem asChild>
            <Link to="/">
              <HomeIcon /> {personal} {mark(personal)}
            </Link>
          </DropdownMenuItem>
          {orgs.map((o) => (
            <DropdownMenuItem asChild key={o.id}>
              <Link to={`/orgs/${encodeURIComponent(o.name)}`}>
                <OrganizationIcon /> {o.name} {mark(o.name)}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuGroup className="p-2">
          <DropdownMenuItem asChild>
            <Link to="/orgs/new">
              <PlusIcon /> New organization
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({
  actor,
  theme,
  onToggleTheme,
  onSignOut,
  side = "bottom",
  align = "end",
}: {
  actor: Actor;
  theme: Theme;
  onToggleTheme: () => void;
  onSignOut: () => void;
  side?: "top" | "bottom";
  align?: "start" | "end";
}) {
  const user = useMe().data?.user;
  const avatar = (size: number) =>
    user ? <AvatarImage src={user.avatar_url} name={user.name} size={size} /> : <Avatar name={actor.sub} size={size} agent={actor.kind !== "admin"} />;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex shrink-0 rounded-full hover:opacity-80 data-[state=open]:opacity-80" aria-label="Open user menu">
        {avatar(32)}
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="w-70 py-0">
        <div className="flex items-center gap-3 px-4 pb-3 pt-4">
          {avatar(40)}
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold" title={actor.sub}>
              {actor.sub}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Badge>{actor.kind}</Badge>
              {actor.task && (
                <span className="truncate" title={actor.task}>
                  {actor.task}
                </span>
              )}
            </div>
          </div>
        </div>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuGroup className="p-2">
          <DropdownMenuItem asChild>
            <Link to="/profile">
              <HomeIcon /> Your profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              onToggleTheme();
            }}
          >
            {theme === "dark" ? <SunIcon /> : <MoonIcon />}
            {theme === "dark" ? "Light theme" : "Dark theme"}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuGroup className="p-2">
          <DropdownMenuItem variant="destructive" onSelect={onSignOut}>
            <SignOutIcon /> Sign out
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
